import {
  Email as EmailIcon,
  FlashOn as EnergyIcon,
  AccountTree as JourneyIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";

import { CommunicationTypeEnum } from "../../../store/services/communicationService";
import { MyCustomerApiResponse } from "../../../store/services/customerService";
import {
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../../utils/workItemUtils";
import { CardFieldConfig, CustomerDataField } from "./CustomerColumnConfig";
import CustomerContextMenu, { CustomerAction } from "./CustomerContextMenu";
import {
  formatFullDate,
  formatTimeAgo,
  getLoginTrend,
  getMostRecentLogin,
} from "./dateUtils";
import { getChipColor, getTrendDescription, getTrendIcon } from "./helpers";

interface CustomerCardProps {
  customer: MyCustomerApiResponse;
  onClick: () => void;
  onContextMenu?: (event: React.MouseEvent) => void;
  fieldConfig: CardFieldConfig[];
}

// Format consumption to readable format
const formatConsumption = (consumption: number | null) => {
  if (consumption === null) return null;

  // Format with thousands separators and add kWh suffix
  return `${consumption.toLocaleString("sv-SE")} kWh`;
};

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  onClick,
  onContextMenu,
  fieldConfig,
}) => {
  const theme = useTheme();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    customer: MyCustomerApiResponse;
  } | null>(null);

  // Get most recent login date
  const mostRecentLogin = getMostRecentLogin(customer);

  // Get login trend
  const loginTrend = getLoginTrend(customer);

  // Get primary contact methods
  const primaryEmail = customer.Communications?.find(
    (comm) => comm.Type === CommunicationTypeEnum.Epost && comm.IsPrimary
  );

  const primaryPhone = customer.Communications?.find(
    (comm) => comm.Type === CommunicationTypeEnum.Mobiltelefon && comm.IsPrimary
  );

  // Format timestamps
  const formattedLastContact = customer.LastAction?.ActionDate
    ? formatTimeAgo(customer.LastAction.ActionDate)
    : null;

  const formattedNextAction = customer.NextAction?.ActionDate
    ? formatTimeAgo(customer.NextAction.ActionDate)
    : null;

  const formattedLastLogin = mostRecentLogin
    ? formatTimeAgo(mostRecentLogin)
    : null;

  // Full date format for tooltips
  const fullDateLastLogin = mostRecentLogin
    ? formatFullDate(mostRecentLogin)
    : null;

  // Format consumption
  const formattedConsumption = formatConsumption(customer.ActiveFVolume);

  // Sortera fältkonfigurationen efter prioritet
  const sortedFieldConfig = [...fieldConfig].sort(
    (a, b) => (a.priority || 100) - (b.priority || 100)
  );

  // Gruppera fält efter sektion
  const headerFields = sortedFieldConfig.filter(
    (field) => field.section === "header"
  );
  const metadataFields = sortedFieldConfig.filter(
    (field) => field.section === "metadata"
  );
  const activityFields = sortedFieldConfig.filter(
    (field) => field.section === "activity"
  );

  // Handle right-click context menu
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // If onContextMenu prop is provided, call it (custom context menu)
    if (onContextMenu) {
      onContextMenu(event);
      return;
    }

    // Otherwise, show the default context menu
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      customer,
    });
  };

  // Close context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Handle context menu actions
  const handleMenuAction = (action: CustomerAction) => {
    switch (action) {
      case "viewDetails":
        onClick();
        break;
      case "copyId":
        navigator.clipboard.writeText(customer.CustomerId);
        break;
      case "copyName":
        navigator.clipboard.writeText(customer.Name);
        break;
      case "createActivity":
        // Show activity creation dialog (previously scheduleAction)
        if (onContextMenu) {
          const fakeEvent = {
            preventDefault: () => {},
            stopPropagation: () => {},
          } as React.MouseEvent;
          onContextMenu(fakeEvent);
        }
        break;

        // Any custom handler for update segment
        break;
    }
  };

  // Rendera ett fält baserat på dess typ
  const renderField = (field: CardFieldConfig) => {
    // Använd anpassad renderingsfunktion om den finns
    if (field.renderField) {
      return field.renderField(customer);
    }

    // Annars använd standardrendering baserat på fälttyp
    switch (field.field) {
      case CustomerDataField.NAME:
        return (
          <Typography variant="h6" component="h2" noWrap>
            {customer.Name}
          </Typography>
        );

      case CustomerDataField.CUSTOMER_ID:
        return (
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {customer.CustomerId}
          </Typography>
        );

      case CustomerDataField.SEGMENT:
        return customer.CustomerSegmentName ? (
          <Chip
            label={customer.CustomerSegmentName}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              bgcolor: getChipColor(customer.CustomerSegmentName, theme),
              color: theme.palette.getContrastText(
                getChipColor(customer.CustomerSegmentName, theme)
              ),
            }}
          />
        ) : null;

      case CustomerDataField.JOURNEY_TYPE:
        return customer.B2bJourneyType ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <JourneyIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap>
              {customer.B2bJourneyType}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.CONSUMPTION:
        return formattedConsumption ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EnergyIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap>
              {formattedConsumption}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.EMAIL:
        return primaryEmail ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
              {primaryEmail.Value}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.PHONE:
        return primaryPhone ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{primaryPhone.Value}</Typography>
          </Box>
        ) : null;

      case CustomerDataField.SALES_REP:
        return customer.SalesResponisble ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap>
              {customer.SalesResponisble}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.POSTAL_CITY:
        return customer.PostalCity ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap>
              {customer.PostalCity}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.LAST_CONTACT:
        return formattedLastContact ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {field.label || "Senast kontakt"}: {formattedLastContact}
            </Typography>
          </Box>
        ) : null;

      case CustomerDataField.LAST_LOGIN:
        return formattedLastLogin ? (
          <Tooltip
            title={`${fullDateLastLogin} - ${getTrendDescription(loginTrend)}`}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TimeIcon fontSize="small" color="action" />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: 0.5 }}
                >
                  {field.label || "Senast inloggad"}: {formattedLastLogin}
                </Typography>
                {getTrendIcon(loginTrend)}
              </Box>
            </Box>
          </Tooltip>
        ) : null;

      case CustomerDataField.NEXT_ACTION:
        return customer.NextAction?.ActionDate ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: theme.palette.background.default,
              borderRadius: 1,
              p: 1,
              width: "100%",
            }}
          >
            {getWorkItemIcon(customer.NextAction.ActionType)}
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {field.label || "Nästa aktivitet"}:{" "}
                {customer.NextAction.Text ||
                  getWorkItemTypeName(customer.NextAction.ActionType)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formattedNextAction}
              </Typography>
            </Box>
          </Box>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <>
      <Card
        sx={{
          mb: 2,
          p: 2,
          cursor: "pointer",
          transition: "0.2s",
          "&:hover": {
            boxShadow: 4,
            bgcolor: theme.palette.action.hover,
          },
        }}
        onClick={onClick}
        onContextMenu={handleContextMenu}
      >
        {/* Header-sektion (namn, kundnummer, segment) */}
        <Box sx={{ mb: 1 }}>
          {headerFields.map((field) => (
            <Box key={field.field}>{renderField(field)}</Box>
          ))}
        </Box>

        <Stack spacing={1.5}>
          {/* Metadata-sektion (kundtyp, förbrukning, kontakter) */}
          {metadataFields.length > 0 && (
            <Grid container spacing={1}>
              {metadataFields.map((field) => {
                const content = renderField(field);
                return content ? (
                  <Grid item xs={6} key={field.field}>
                    {content}
                  </Grid>
                ) : null;
              })}
            </Grid>
          )}

          <Divider light />

          {/* Aktivitetssektion (senast kontakt, senast inloggad) */}
          {activityFields.filter(
            (f) => f.field !== CustomerDataField.NEXT_ACTION
          ).length > 0 && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {activityFields
                .filter((f) => f.field !== CustomerDataField.NEXT_ACTION)
                .map((field) => {
                  const content = renderField(field);
                  return content ? (
                    <Box key={field.field}>{content}</Box>
                  ) : null;
                })}
            </Box>
          )}

          {/* Nästa aktivitet visas alltid sist och tar hela bredden */}
          {activityFields
            .filter((f) => f.field === CustomerDataField.NEXT_ACTION)
            .map((field) => {
              const content = renderField(field);
              return content ? <Box key={field.field}>{content}</Box> : null;
            })}
        </Stack>
      </Card>

      {/* Only render the default context menu if no custom one is provided */}
      {!onContextMenu && (
        <CustomerContextMenu
          contextMenu={contextMenu}
          onClose={handleCloseContextMenu}
          onAction={handleMenuAction}
        />
      )}
    </>
  );
};

export default CustomerCard;
